import { AxiosError } from 'axios';
import React from 'react';
import { Link } from 'react-aria-components';
import { useForm } from 'react-hook-form';

import { BackActionButton } from '#/shared/components/BackActionButton';
import { Card } from '#/shared/components/Card';
import { FixedButton } from '#/shared/components/FixedButton';
import { FormControlMultiTextInput, FormControlTextArea, FormControlTextInput } from '#/shared/components/FormControl';
import { MaterialIcon } from '#/shared/components/MaterialIcon';
import { appConfig } from '#/shared/config/AppConfig';
import { useAnalytics } from '#/shared/hooks/useAnalytics';
import { useMonitoring } from '#/shared/hooks/useMonitoring';
import { usePageStateStore } from '#/shared/stores/pageStateStore';
import { useTimesheetActionViewStore } from '#/shared/stores/timesheetActionViewStore';
import { useTimesheetStore } from '#/shared/stores/timesheetStore';
import { validateEmail, validateRequiredField } from '#/shared/utils/formHelper';

import { mapForwardRequestFormValuesToForwardRequestApiData } from '../adaptors/mapForwardRequestFormValuesToForwardRequestApiData';
import { useForwardRequestMutation } from '../hooks/useForwardRequestMutation';
import { useForwardFormStore } from '../stores/forwardFormStore';
import { ForwardRequestApiErrorResponseData } from '../types/ForwardRequestApi';
import { ForwardRequestFormValues } from '../types/ForwardRequestFormValues';

export function ForwardRequestForm() {
  const { noticeError } = useMonitoring();
  const { track } = useAnalytics();
  const timesheetId = useTimesheetStore(state => state.timesheet?.timesheetId ?? '');
  const setView = useTimesheetActionViewStore(state => state.setView);
  const setPageState = usePageStateStore(state => state.setPageState);
  const forwardFormData = useForwardFormStore(state => state.formData);
  const setForwardFormData = useForwardFormStore(state => state.setFormData);

  const { handleSubmit, control, register, getValues } = useForm<ForwardRequestFormValues>({
    defaultValues: {
      requesteeName: forwardFormData?.requesteeName ?? '',
      requesteeEmail: forwardFormData?.requesteeEmail ?? '',
      forwarderName: forwardFormData?.forwarderName ?? '',
      forwarderEmails: forwardFormData?.forwarderEmails ?? [{ email: '' }],
      forwarderMessage: forwardFormData?.forwarderMessage ?? '',
    },
    mode: 'onSubmit',
  });

  React.useEffect(() => {
    // Save form data to store on unmount
    return () => {
      setForwardFormData(getValues());
    };
  }, [getValues, setForwardFormData]);

  const forwardRequest = useForwardRequestMutation();
  const [isSubmissionError, setIsSubmissionError] = React.useState(false);
  const [isSubmissionPending, setIsSubmissionPending] = React.useState(false);

  const handleBackToActions = () => {
    track('Online approval web - Press Back to timesheet actions link', {
      Timesheet_ID: timesheetId,
      Source_Screen: 'Forward approval',
    });

    setView('actions');
  };

  const onSubmit = async (formData: ForwardRequestFormValues) => {
    setIsSubmissionError(false);
    setIsSubmissionPending(true);

    try {
      const data = mapForwardRequestFormValuesToForwardRequestApiData(formData);

      await forwardRequest.mutateAsync({ data });

      track('Online approval web - Forward request success', {
        Timesheet_ID: timesheetId,
      });

      setIsSubmissionPending(false);
      setPageState('forwarding-success');
    } catch (error) {
      setIsSubmissionError(true);
      setIsSubmissionPending(false);

      track('Online approval web - Forward request failure', {
        Timesheet_ID: timesheetId,
      });

      const expectedError = error as AxiosError<ForwardRequestApiErrorResponseData>;
      noticeError(new Error('Online approval request forwarding has failed'), {
        originalMessage: expectedError?.message,
        originalData: expectedError?.response?.data,
        originalError: expectedError,
        timesheetId,
      });
    }
  };

  return (
    <Card className="max-tablet-portrait:py-32 max-tablet-portrait:pl-20 max-tablet-portrait:pr-20 max-tablet-portrait:border-0 max-tablet-portrait:rounded-none max-tablet-portrait:border-y-1 max-tablet-portrait:bg-primary-surface-l1">
      <BackActionButton onPress={handleBackToActions}>Back to timesheet actions</BackActionButton>

      <form className="gap-8 flex flex-col" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <h2 className="text-24 font-600 mb-8">Forward approval request</h2>
        </div>

        <div className="gap-16 flex flex-col mb-16">
          <FormControlTextInput
            label="New approver's full name"
            placeholder="Enter text"
            name={'requesteeName'}
            type="text"
            control={control}
            rules={{
              validate: {
                required: validateRequiredField,
              },
            }}
            errorLeadingIconName={'error'}
          />

          <FormControlTextInput
            label="New approver's email address"
            description="By forwarding, you confirm that you are authorised to share this email address."
            descriptionPosition="above"
            placeholder="Enter email"
            name={'requesteeEmail'}
            type="text"
            control={control}
            rules={{
              validate: {
                email: validateEmail,
                required: validateRequiredField,
              },
            }}
            errorLeadingIconName={'error'}
          />

          <FormControlTextInput
            label="Your name"
            description="Used only to inform the new approver of who forwarded the request."
            descriptionPosition="above"
            placeholder="Enter text"
            name={'forwarderName'}
            type="text"
            control={control}
            rules={{
              validate: {
                required: validateRequiredField,
              },
            }}
            errorLeadingIconName={'error'}
          />
        </div>

        <div className="gap-16 flex flex-col">
          <h3 className="text-20 text-left font-600">Optional</h3>

          <FormControlMultiTextInput
            name="forwarderEmails"
            control={control}
            register={register}
            validate={validateEmail}
            label="Your email address (optional)"
            description="If you'd like to receive a copy of the approved timesheet."
            placeholder="Enter email"
            addFieldButtonLabel="Add another email address"
            type="text"
            fieldKey="email"
            minFields={1}
            maxFields={5}
          />

          <FormControlTextArea
            label="Message for new approver (optional)"
            placeholder="Enter text"
            name={'forwarderMessage'}
            control={control}
            maxLength={500}
            rules={{
              maxLength: { value: 500, message: 'Maximum allowed characters is 500' },
            }}
            errorLeadingIconName={'error'}
          />
        </div>

        {isSubmissionError && (
          <div className="text-14 text-error text-left flex gap-4">
            <MaterialIcon iconName={'error'} size={'sm'} />
            <p>
              Sorry, unable to forward approval request. Please try again later or forward the approval request email
              directly. Contact{' '}
              <Link
                href={`mailto:${appConfig.feedback.email}`}
                className="text-primary hover:text-primary-shade focus:text-primary-shade hover:underline"
              >
                {appConfig.feedback.email}
              </Link>{' '}
              if the issue persists.
            </p>
          </div>
        )}

        <div className="pt-8">
          <FixedButton type="submit" disabled={isSubmissionPending} isLoading={isSubmissionPending}>
            Forward approval request
          </FixedButton>
        </div>
      </form>
    </Card>
  );
}
